/**
 * Component: Product gallery
 * ------------------------------------------------------------------------------
 * An Product gallery file that contains scripts to function.
 *
 * @namespace productGallery
 */
/* eslint-disable */

import breakpoints from '~/helpers/breakpoints';
import cssClasses from '~/helpers/cssClasses';
import {debounce, on} from '~/helpers/utils';

import Carousel from '~/components/carousel';
import scrollDots from '~/components/scroll-dots';
import imageVideo from '~/components/image-video';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-product-gallery="container"]',
  featuredImage: '[js-product-gallery="featuredImage"]',
  featuredCarousel: '[js-product-gallery="featuredCarousel"]',
  dots: '[js-product-gallery="dot"]',
  dotContainer: '[js-product-gallery="dotContainer"]',
  optionInput: '[js-product-swatches="optionInput"]',
  video: '[js-video-image="gallery"]',
  engravingCheckbox: '[js-engraving="checkbox"]',
  galleryImages: '[data-variant-image-id]',
  proxyImagesContainer: '[data-variant-image-proxy-container]',
  dotAltText: '[data-dot-alt-text]',
  masterSelector: '.product-form[action="/cart/add"] [name="id"]'
};

/**
 * Attribute and data selectors.
 */
const dataSelectors = {
  dots: 'js-product-gallery',
};

/**
 * Export a new product gallery.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    featuredImage: [...document.querySelectorAll(selectors.featuredImage)],
    featuredCarousel: document.querySelector(selectors.featuredCarousel),
    dots: [...document.querySelectorAll(selectors.dots)],
    dotContainer: document.querySelector(selectors.dotContainer),
    video: document.querySelector(selectors.video),
    engravingCheckbox: document.querySelector(selectors.engravingCheckbox),
    proxyImagesContainer: document.querySelector(selectors.proxyImagesContainer),
    currentVariantInput: document.querySelector(selectors.masterSelector)
  };

  const dotsConfig = {
    container: nodeSelectors.container,
    dotContainer: nodeSelectors.dotContainer,
    dots: nodeSelectors.dots,
  };

  /**
   * Instance globals.
   */
  const namespace = nodeSelectors.featuredCarousel;
  let featuredCarousel = {};
  let imagesAnimating = false;

  /**
   * Initialise component.
   */
  function init() {
    setDefaultState();
    setEventBusListeners();
    setEventListeners();
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    on('resize', debounce(handleResizeEvent, 250));
  }

  function smoothReveal(variantId, onEnd = function(){}) {
    window.clearTimeout(imagesAnimating);

    const currentlyVisibleVariantImages = [...nodeSelectors.container.querySelectorAll(selectors.galleryImages)];
    const proxyVariantImage = nodeSelectors.proxyImagesContainer.querySelector(`[data-variant-image-id="${variantId}"]`);

    if(proxyVariantImage) {
      if(currentlyVisibleVariantImages) { // Check if there actually is variant images in the currently viewed gallery
        for (let i = 0; i < currentlyVisibleVariantImages.length; ++i) {
          const imageWrapper = currentlyVisibleVariantImages[i].querySelector('.product-gallery__featured');

          if(proxyVariantImage && imageWrapper) {
            // Check if there actually is a variant image on next variant
            const newImageEl = proxyVariantImage.querySelector('.product-gallery__image');
            const oldImageEl = imageWrapper.querySelector('.product-gallery__image');

            if(newImageEl) {
              const imageClone = newImageEl.cloneNode(true);
              imageWrapper.append(imageClone);
              imageClone.style.opacity = 0;

              imageClone.addEventListener('transitionend', function(){
                oldImageEl.remove();
                onEnd();
              });

              imagesAnimating = window.setTimeout(function(){
                window.requestAnimationFrame(function(){
                  if(imageClone) {
                    oldImageEl.style.opacity = 0;
                    imageClone.style.opacity = 1;
                  }
                });
              }, 10);
            }
          }
        }
      } else {
        const swiperWrapper = nodeSelectors.container.querySelector('.swiper-wrapper');
        const imageClone = proxyVariantImage.cloneNode(true);
        swiperWrapper.prepend(imageClone);

        currentlyVisibleVariantImages.map(slide => slide.remove());
      }
    }
  }

  function updateDots() {
    if(nodeSelectors.dots.length) {
      let dotTemplate = nodeSelectors.dots[0];
      dotTemplate.classList.remove(cssClasses.active);
      nodeSelectors.dotContainer.innerHTML = ""; // Empty dot container
      const newVisibleVariantImages = [...nodeSelectors.container.querySelectorAll(selectors.featuredImage)];

      newVisibleVariantImages.map((image, i) => {
        const imageId = image.getAttribute('data-image-id');
        const newDot = dotTemplate.cloneNode(true);

        // Set the dot attributes
        if(i === 0) newDot.classList.add(cssClasses.active);
        newDot.setAttribute('data-image', imageId);

        const altTextNode = newDot.querySelector(selectors.dotAltText);
        if(altTextNode) {
          altTextNode.innerHTML = `Image ${i}`;
        }

        nodeSelectors.dotContainer.append(newDot);
      });

      const newDots = [...document.querySelectorAll(selectors.dots)];

      // Resetting dots ids
      newDots.map((newDot, i) => newDot.setAttribute('data-id', i));

      const newDotsConfig = {
        container: nodeSelectors.container,
        dotContainer: nodeSelectors.dotContainer,
        dots: newDots,
      }

      scrollDots(newDotsConfig).init();
    }
  }

  /**
   * Listen for product form change events.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('ProductSwatches:optionSelected', ({target}) => {
      if(nodeSelectors.container && nodeSelectors.proxyImagesContainer) {

        smoothReveal(target.dataset.variantId, function(){
          // Below large bp
          if (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
            destroyCarousels();
            constructCarousels();
            featuredCarousel.init();

            // Refresh references to slider elements
            nodeSelectors.dots = [...document.querySelectorAll(selectors.dots)];
            nodeSelectors.dotsContainer = document.querySelector(selectors.dotContainer);
            setDotClickEvents();
          } else {
            // Over large bp
            updateDots();
          }
        });
      }
    });

    if(nodeSelectors.container && nodeSelectors.proxyImagesContainer) {
      document.addEventListener('Alessi:formStateUpdate', () => {
        const variantId = document.querySelector('[name="id"]').value;
        smoothReveal(variantId, function(){
          // Below large bp
          if (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
            destroyCarousels();
            constructCarousels();
            featuredCarousel.init();

            // Refresh references to slider elements
            nodeSelectors.dots = [...document.querySelectorAll(selectors.dots)];
            nodeSelectors.dotsContainer = document.querySelector(selectors.dotContainer);
            setDotClickEvents();
          } else {
            // Over large bp
            updateDots();
          }
        });
      });
    }

    Frame.EventBus.listen(`Carousel-${namespace.id}:settle`, ({selectedIndex}) => {
      const selectedIndexDot = (selectedIndex) ? selectedIndex : "0";
      const selectedDot = document.querySelector(
        `${selectors.dots}[data-id="${selectedIndexDot}"]`,
      );

      updateSelectedState(selectedDot);
    });
  }

  /**
   * Construct carousel objects on gallery images.
   */
  function constructCarousels() {
    featuredCarousel = Carousel(selectors.featuredCarousel, {
      swiper: {
        autoHeight: true,
        loop: false,
        spaceBetween: 10,
      },
    });
  }

  /**
   * Set default state based on screen size.
   */
  function setDefaultState() {
    constructCarousels();
    initCarousels();
    setDotClickEvents();

    if (!nodeSelectors.video) { return; }
    imageVideo({container: nodeSelectors.video}).init();
  }

  /**
   * Initialise gallery.
   */
  function initCarousels() {
    if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
      scrollDots(dotsConfig).init();
      return;
    }

    featuredCarousel.init();
  }

  function destroyCarousels() {
    if (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
      featuredCarousel.destroy();
    }
  }

  /**
   * Handle resize events through debounce callback.
   * - Remove carousel on dots on large screens.
   */
  function handleResizeEvent() {
    initCarousels();

    if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
      featuredCarousel.destroy();
    } else {
      setDotClickEvents();
    }
  }

  /**
   * Set dot event handlers to update on click.
   */
  function setDotClickEvents() {
    on('click', nodeSelectors.dotsContainer, handleDotClick);
    on('click', nodeSelectors.engravingCheckbox, handleDotClick);
  }


  /**
   * Handle dot image click.
   * @param {Event} event - Click event on image dots.
   */
  function handleDotClick(event) {
    const target = event.target.closest(selectors.dots);
    if (isTargetDot(target)) {

      if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
        return;
      }

      updateSelectedState(target);
      updateFeaturedImage(target.dataset.id);
    }

  }


  /**
   * Detect quick view toggle click.
   * @param {HTMLElement} target - Clicked target to check quick view attributes against.
   * @returns {Boolean}
   */
  function isTargetDot(target) {
    return (
      target !== null &&
      target.attributes[dataSelectors.dots].nodeValue === 'dot'
    );
  }

  /**
   * Update state of selected dot target.
   * @param {Object} target - Selected dot element.
   */
  function updateSelectedState(target) {
    nodeSelectors.dots.forEach((element) => {
      element.classList.remove(cssClasses.active);
    });
    target.classList.add(cssClasses.active);
  }

  /**
   * Switch main dot image.
   * @param {Number} id - Target dot id to switch to.
   */
  function updateFeaturedImage(id) {
    featuredCarousel.select(id);
  }

  /**
   * Check if variant image is available.
   * @param {Number} id - Variant id of selected option.
   * @returns {Boolean}
   */
  function isVariantImageAvailable(id) {
    return Boolean(
      document.querySelector(`${selectors.dots}[data-variant-id="${id}"`),
    );
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    isVariantImageAvailable,
    updateFeaturedImage,
  });
};
